import {
	useEligibleForJsmLandUseCaseOnboarding,
	useEligibleForTwcg1935CodingAgentChoice,
	useLoggedInBitbucket,
	useLoggedOut,
	useLoggedOutConfluence,
	useLoggedInHasJiraSiteD1toD14,
	useIsSoftwareConfluencePath,
	useIsSoftwareJiraPath,
	useLoggedOutJira,
	useExcludeJiraImcCampaign,
	useLoggedInHasJira,
	useNewUser,
	useSpecifiedPathnames,
} from '@/components/user-context/helpers/cohorts';

import { Manifest } from './use-experiment';
import {
	wacRefreshConfluence,
	wacRefreshHomepage,
	wacRefreshJira,
	wacRefreshTwcRovo,
} from './wac-refresh-experiments';

const booleanTypeGuard = (value: unknown): value is boolean => typeof value === 'boolean';

export const experiments = {
	gwp9322WacHomepageRefresh: {
		experimentName: 'gwp-9322-wac-homepage-refresh-experiment',
		parameterName: 'variantName',
		defaultValue: 'control' as 'control' | 'test',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'test' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'test'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Teamwork Collection refresh changes on homepage',
			expectedReleaseDate: '01/15/2026',
			expectedRuntime: 'indefinite',
			context: {
				urls: ['/'],
			},
		},
	},
	// GWP-12151: A/B/C test of the 1mind.ai "superhuman" AI chat vs the current
	// Omnichannel widget on the JPD product-discovery page. Three equal cells:
	//   - control:  current experience (Omnichannel)
	//   - ai_guide: 1mind "AI Guide" persona (waveform)  -> deployment-rprgpn5ltf
	//   - stephen:  1mind "Stephen" persona (human)       -> deployment-fhn85kw52t
	// NOTE: `experimentName`, `parameterName` and the three cell values below MUST
	// match the Statsig console experiment exactly, or getExperimentValue returns
	// undefined and every user silently falls back to `control`.
	gwp12151Superhuman1mind: {
		experimentName: 'gwp-12151-1mind-superhuman',
		parameterName: 'variantName',
		defaultValue: 'control' as 'control' | 'ai_guide' | 'stephen',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'ai_guide' | 'stephen' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'ai_guide', 'stephen'].includes(value);
			},
		},
		targeting: {
			cohort: () => {
				// oxlint-disable-next-line react-hooks/rules-of-hooks
				return useSpecifiedPathnames(['/software/jira/product-discovery']);
			},
		},
		metadata: {
			description:
				'A/B/C test of the 1mind superhuman AI chat (AI Guide / Stephen personas) vs the Omnichannel widget on the JPD product-discovery page',
			expectedReleaseDate: 'TBD',
			expectedRuntime: '9 weeks',
			context: {
				urls: ['/software/jira/product-discovery'],
			},
		},
	},
	cfind4782LandingPageSubAnchorV3: {
		experimentName: 'cfind-4782-landing-page-sub-anchor-v3',
		parameterName: 'enrolled',
		defaultValue: false,
		options: {
			fireExperimentExposure: false,
			typeGuard: (value: unknown): value is boolean => typeof value === 'boolean',
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Test if personalizing the landing page off the sub anchor will increase d1to6',
			expectedReleaseDate: '07/14/2025',
			expectedRuntime: '6 weeks',
			// TODO: GWP-5270 to fill these urls
			context: {
				urls: [],
			},
		},
	},
	signupAddPaymentAb: {
		experimentName: 'signup_trialing_add_payment_ab',
		parameterName: 'cohort',
		defaultValue: 'control' as 'control' | 'test',
		options: {
			// we need to fire our exposure event manually because we can't determine if the user is eligible from here
			fireExperimentExposure: false,
			typeGuard: (value: unknown): value is 'control' | 'test' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'test'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Experiment to enable users with valid sites to go to payment at the sign-up screen',
			expectedReleaseDate: '02/27/25',
			expectedRuntime: 'TBD',
			// TODO: GWP-5270 to fill these urls
			context: {
				urls: [],
			},
		},
	},
	gwp5090ExperimentBannerConfluence: {
		experimentName: 'gwp-5090-experiment-banner-confluence',
		parameterName: 'variantName',
		defaultValue: 'control' as 'control' | 'butterbar' | 'lifted',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'butterbar' | 'lifted' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'butterbar', 'lifted'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Testing butterbar vs lifted banner for Jira Service Management product tour',
			expectedReleaseDate: '02/18/25',
			expectedRuntime: '2 weeks',
			// TODO: GWP-5270 to fill these urls
			context: {
				urls: [],
			},
		},
	},
	gwp7617JiraShortTermAiInfusionExperiment: {
		experimentName: 'gwp-7617-jira-short-term-ai-infusion-experiment',
		parameterName: 'variantName',
		defaultValue: 'control' as 'control' | 'test',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'test' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'test'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Short term AI',
			expectedReleaseDate: '08/21/25',
			expectedRuntime: 'indefinite',
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	gwp4948GartnerJiraProductTourExperiment: {
		experimentName: 'gwp-4948-gartner-jira-product-tour-experiment',
		parameterName: 'variantName',
		defaultValue: 'control' as 'control' | 'test',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'test' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'test'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'gartner section on jira product tour',
			expectedReleaseDate: 'April 2025',
			expectedRuntime: '2 weeks',
			clientOnly: true,
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	gwp6441ConfluenceTemplatesProductNavCta: {
		experimentName: 'gwp-6441-confluence-templates-product-nav-cta',
		parameterName: 'variant',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => {
				// oxlint-disable-next-line react-hooks/rules-of-hooks
				return !useLoggedOut();
			},
		},
		metadata: {
			description: 'Confluence Templates Product Nav CTA experiment',
			expectedReleaseDate: 'undefined',
			expectedRuntime: '77 days',
			clientOnly: true,
			context: {
				urls: ['/software/confluence/templates/*'],
			},
		},
	},
	twcg2273CustomersPagePrimaryCta: {
		experimentName: 'twcg-2273-customers-page-primary-cta',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedOut,
		},
		metadata: {
			description: 'Customers page primary CTA click-through experiment',
			expectedReleaseDate: '07/29/2026',
			expectedRuntime: '28 days',
			clientOnly: true,
			context: {
				urls: ['/customers'],
			},
		},
	},
	twcg2271BitbucketSignupPrValue: {
		experimentName: 'wac_bitbucket_signup_pr_value',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedInBitbucket,
		},
		metadata: {
			description: 'Bitbucket signup PR value copy experiment for logged-in users (TWCG-2271)',
			expectedReleaseDate: '08/05/2026',
			expectedRuntime: '4 weeks',
			clientOnly: true,
			context: {
				urls: ['/try/cloud/signup'],
			},
		},
	},
	wacRoutingTableMvpPerf: {
		experimentName: 'wac-routing-table-mvp',
		parameterName: 'enrolled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is boolean => typeof value === 'boolean',
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Measure performance degredation of wac-routing-table in wac-web',
			expectedReleaseDate: '08/28/2025',
			expectedRuntime: '5 days',
			context: {
				urls: ['/software/jira/enterprise'],
			},
		},
	},
	confluencePricingPageWaeMigration: {
		experimentName: 'confluence_pricing_wae_migration_exp_rerun_exp_1',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Migration of Confluence pricing page to WAE, rerun',
			expectedReleaseDate: '03/06/2026',
			expectedRuntime: '80 days',
			context: {
				urls: ['/software/confluence/pricing'],
			},
		},
	},
	gwp8288PersonalizingWacSoftwarePage: {
		experimentName: 'gwp-8288-personalizing-wac-software-page',
		parameterName: 'enrolled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Personalizing WAC Software Page Experiment',
			expectedReleaseDate: '10/30/2025',
			expectedRuntime: '6 weeks',
			context: {
				urls: ['/software'],
			},
		},
	},
	twcg790ContextualBackground: {
		experimentName: 'twcg-790-contextual-backgrounds-wac-sign-in',
		parameterName: 'cohort',
		defaultValue: 'control' as 'control' | 'test',
		options: {
			fireExperimentExposure: true,
			typeGuard: (value: unknown): value is 'control' | 'test' => {
				if (typeof value !== 'string') {
					return false;
				}
				return ['control', 'test'].includes(value);
			},
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'adding atlOrigin and application query strings to IDAC login URL to enable contextual background on sign in page',
			expectedReleaseDate: '01/12/2025',
			expectedRuntime: '7 weeks',
			clientOnly: true,
			context: {
				// eslint-disable-next-line
				urls: ['*'],
			},
		},
	},
	jiraPricingWaeMigrationExperimentRerunExp: {
		experimentName: 'jira_pricing_wae_migration_experiment_rerun_exp_1',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Rerun of migration of Jira pricing page to WAE',
			expectedReleaseDate: '03/06/2026',
			expectedRuntime: '43 days',
			context: {
				urls: ['/software/jira/pricing'],
			},
		},
	},
	dxpMigrationConfluenceAnchorJourney: {
		experimentName: 'dxp_migration_confluence_anchor_journey',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Migration of Confluence anchor journey to DXP',
			expectedReleaseDate: 'TBD',
			expectedRuntime: 'TBD',
			context: {
				urls: ['/software/confluence'],
			},
		},
	},
	gwp9508ConfluenceBannerIosNativeApp: {
		experimentName: 'gwp-9508-confluence-banner-ios-native-app',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useIsSoftwareConfluencePath,
		},
		metadata: {
			description: 'Confluence Banner Ios Native App Experiment',
			expectedReleaseDate: '01/22/2026',
			expectedRuntime: '39 days',
			context: {
				urls: ['/software/confluence/*'],
			},
		},
	},
	gwp9507JiraBannerIosNativeApp: {
		experimentName: 'gwp-9507-jira-banner-ios-native-app',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useIsSoftwareJiraPath,
		},
		metadata: {
			description: 'Jira Banner App Experiment',
			expectedReleaseDate: '01/22/2026',
			expectedRuntime: '60 days',
			context: {
				urls: ['/software/jira/*'],
			},
		},
	},
	d1d6redirectLoggedInUsers: {
		experimentName: 'd1d6-151-redirect-logged-in-users',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedInHasJira,
		},
		metadata: {
			description: 'Redirect logged in users to most recent jira site',
			expectedReleaseDate: '07/27/2026',
			expectedRuntime: '',
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	d1d6218AiNativeNbps: {
		experimentName: 'd1d6-218-ai-native-nbps',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			// Single paid-channel experiment: it now covers ALL logged-out paid
			// search traffic on /software/jira — both branded (BPS) and
			// non-branded (NBPS). Exposure is fired manually; branded vs
			// non-branded segmentation is handled by the Statsig gate on the
			// experiment.
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedOut,
		},
		metadata: {
			description:
				'Split /software/jira traffic for logged-out paid search users (branded/BPS and non-branded/NBPS combined); treatment serves the WAE (Uniform) landing page.',
			expectedReleaseDate: '07/29/2026',
			expectedRuntime: 'TBD',
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	cfind6214WacLandingForAiNativeTeamsPt: {
		experimentName: 'cfind-6214-wac-landing-for-ai-native-teams-pt',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => {
				return (
					// oxlint-disable-next-line react-hooks/rules-of-hooks
					useLoggedOut() &&
					// oxlint-disable-next-line react-hooks/rules-of-hooks
					useExcludeJiraImcCampaign()
				);
			},
		},
		metadata: {
			description:
				'Split /software/jira traffic for logged-out PT users (excluding the Jira IMC paid campaign); treatment serves the WAE (Uniform) landing page.',
			expectedReleaseDate: '06/30/2026',
			expectedRuntime: 'TBD',
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	d1d6DeferSiteNaming: {
		experimentName: 'd1d6-defer-site-naming',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedOutJira,
		},
		metadata: {
			description:
				'Remove the explicit site naming step from the Jira signup flow. In treatment, the continueUrl passes deferSiteNaming=true to the confirmation page, which auto-generates the site name and activates the product without a separate site naming step.',
			expectedReleaseDate: 'TBD',
			expectedRuntime: 'TBD',
			context: {
				urls: ['/software/jira', '/try/cloud/signup'],
			},
		},
	},
	jiraWacActivationChecklist: {
		experimentName: 'wac-jira-activation-checklist',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedInHasJiraSiteD1toD14,
		},
		metadata: {
			description:
				"Per-product activation checklist surfaced on wac.com/software/jira, rendered directly below the user's most recently accessed Jira site card. Hosts the @post-office/placements--growth-starburst/wac-product-activation-checklist placement.",
			expectedReleaseDate: '06/01/2026',
			expectedRuntime: '4 weeks',
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	jiraWacActivationChecklistAa: {
		experimentName: 'wac-jira-activation-checklist-aa',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedInHasJiraSiteD1toD14,
		},
		metadata: {
			description:
				'AA test for the Jira activation checklist cohort on wac.com/software/jira. Uses the same D1-D14 Jira-site targeting as the launch experiment to validate exposure volume before rollout.',
			expectedReleaseDate: '06/01/2026',
			expectedRuntime: '2 weeks',
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	gwp10724JiraAgentHeroChangesOnJiraPt: {
		experimentName: 'gwp-10724-jira-agent-hero-changes-on-jira-pt',
		parameterName: 'isEnabled',
		defaultValue: true,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Post-rollout retroactive experiment for visual changes above the fold on the Jira PT, related to Jira Agent release',
			expectedReleaseDate: '2026-03-18',
			expectedRuntime: '24 days',
			clientOnly: false,
			context: {
				urls: ['/software/jira'],
			},
		},
	},
	wacRefreshQ4Fy26AaTest: {
		experimentName: 'wac_refresh_q4fy26_aa_test',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'AA test for WAC Refresh Q4 FY26',
			expectedReleaseDate: 'Team26',
			expectedRuntime: 'TBD',
			context: {
				urls: [
					'/',
					'/software/jira',
					'/software/confluence',
					'/software/rovo',
					'/collections/teamwork',
				],
			},
		},
	},
	servCoLofiBackground: {
		experimentName: 'gwp-9785-servco-lofi-background-signup',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Lofi background for servco signup pages',
			expectedReleaseDate: '04/30/2026',
			expectedRuntime: '4 weeks',
			context: {
				urls: ['/try/cloud/signup?bundle=service-collection', '/ondemand/signup/confirmation'],
			},
		},
	},
	loomLoggedInSignupParity: {
		experimentName: 'loom_logged_in_signup_parity',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Loom logged-in signup: render the lofi Loom-backdrop form (matching logged-out) instead of the default signup for logged-in users on ?bundle=loom',
			expectedReleaseDate: '07/28/2026',
			expectedRuntime: '4 weeks',
			context: {
				urls: ['/try/cloud/signup?bundle=loom'],
			},
		},
	},
	sparkHdyhauSignupExperiment: {
		experimentName: 'spark-hdyhau-signup-experiment',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useNewUser,
		},
		metadata: {
			description: 'HDYHAU signup experiment',
			expectedReleaseDate: 'TBD',
			expectedRuntime: '24 days',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	jfindSampleTasksForLandingGateNonTech: {
		experimentName: 'jfind-sample-tasks-for-landing-gate-non-tech',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Provides sample task data during Jira onboarding for non-technical team types',
			expectedReleaseDate: '',
			expectedRuntime: '',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	twcg1659RemoveWorkTypeScreen: {
		experimentName: 'twcg-1659-remove-work-type-screen',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Remove the Work Types selection step from the Jira onboarding wizard',
			expectedReleaseDate: 'TBD',
			expectedRuntime: '4 weeks',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	twcg1935CodingAgentChoice: {
		experimentName: 'spice-agentic-development-onboarding',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useEligibleForTwcg1935CodingAgentChoice,
		},
		metadata: {
			description: 'Show coding agent choice screen during Jira onboarding',
			expectedReleaseDate: '06/29/2026',
			expectedRuntime: '4 weeks',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	cfindTemplatesBlendedHeroV7: {
		experimentName: 'cfind_templates_blended_hero_v7',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Blended Hero on Confluence templates page — split hero retaining the template search bar (left) alongside a marketing/setup card with value headline, setup steps, and CTAs (right)',
			expectedReleaseDate: '07/28/2026',
			expectedRuntime: '5 weeks',
			context: {
				urls: ['/software/confluence/templates'],
			},
		},
	},
	jsmContextualBanditsInWae: {
		experimentName: 'jsm-contextual-bandits-in-wae',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Contextual bandits in WAE on the JSM product page',
			expectedReleaseDate: '06/25/2026',
			expectedRuntime: 'indefinite',
			context: {
				urls: ['/software/jira/service-management'],
			},
		},
	},
	confluenceContextualBanditsInWae: {
		experimentName: 'confluence-contextual-bandits-in-wae',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Contextual bandits in WAE on the Confluence anchor journey landing pages — personalises above-the-fold headline, subheadline and supporting copy per search/ad intent (GWP-12850, TWCG-2162)',
			expectedReleaseDate: '08/21/2026',
			expectedRuntime: 'indefinite',
			context: {
				urls: ['/software/confluence'],
			},
		},
	},
	jsmGrowth519JsmLandUseCaseOnboarding: {
		experimentName: 'jsmgrowth_519_jsm_land_use_case_onboarding',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			// Exposure is fired only when the Team Choice screen is viewed.
			// Other reads of this experiment must not count exposure.
			fireExperimentExposure: false,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useEligibleForJsmLandUseCaseOnboarding,
		},
		metadata: {
			description:
				'JSM Land use-case driven onboarding — reorders the team choice screen and adds expand/collapse options for JSM new user signups',
			expectedReleaseDate: '07/06/2026',
			expectedRuntime: '8 weeks',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	jfindOnboardIllustrationRefresh: {
		experimentName: 'jfind-onboard-illustration-refresh',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				'Replace the static SVG on the right side of the Jira onboarding wizard with a refreshed illustration component (TWCG-1861)',
			expectedReleaseDate: 'TBD',
			expectedRuntime: '4 weeks',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	cfindConfluencePtInlineSignup: {
		experimentName: 'cfind_confluence_pt_inline_signup',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: useLoggedOutConfluence,
		},
		metadata: {
			description:
				'Test inline signup with social buttons as dominant CTA on WAC Confluence product tour hero section',
			expectedReleaseDate: '08/15/2026',
			expectedRuntime: '4 weeks',
			context: {
				urls: ['/software/confluence'],
			},
		},
	},
	wacSearchEnSignupCta: {
		experimentName: 'wac_search_en_signup_cta',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description: 'Contextual signup CTA card inline with search results on /search for EN locale',
			expectedReleaseDate: '08/07/2026',
			expectedRuntime: '6 weeks',
			context: {
				urls: ['/search'],
			},
		},
	},
	expLoomOnboardingOnWac: {
		experimentName: 'exp-loom-onboarding-on-wac',
		parameterName: 'isEnabled',
		defaultValue: false,
		options: {
			fireExperimentExposure: true,
			typeGuard: booleanTypeGuard,
		},
		targeting: {
			cohort: () => true,
		},
		metadata: {
			description:
				"Replace the provisioning-step loading screen with the Loom onboarding screens on WAC's confirmation page to reduce perceived wait time for new users",
			expectedReleaseDate: '09/11/2026',
			expectedRuntime: '2 weeks',
			context: {
				urls: ['/ondemand/signup/confirmation'],
			},
		},
	},
	wacRefreshJira,
	wacRefreshConfluence,
	wacRefreshHomepage,
	wacRefreshTwcRovo,
} satisfies Manifest<unknown>;
